import React, { useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, reduxForm, formValueSelector } from 'redux-form';

import { TextField, DatePicker } from 'redux-form-material-ui';

import Select from 'components/ui/SelectField';

import * as formatter from 'helpers/Formatter';
import { colors } from 'helpers/ColorHelper';
import ReviewField from 'components/shared/ReviewField';
import ServicesAndUtilitiesWizardPage from 'components/landlord_app/applications/components/lease_creator/servicesAndUtilitiesWizardPage';
import TenantResidentVerbiageHelper from 'components/shared/TenantResidentVerbiageHelper';
import { RenderLeaseItem } from 'components/shared/LeaseItems/helpers';
import { featureEnabled } from 'helpers/ToggleBot';
import { hellosignClient } from 'helpers/hellosignClient';
import env from 'config/constants';
import { getTranslatedString } from 'helpers/i18n';
import { clearClaimUrl } from 'actions/landlordApplicationsActions';
import { showCreateRenewalSuccess } from 'actions/landlordTenantsActions';
import { PAGES } from '../RenewalCreator';

let ReviewPackagePage = ({
  handleSubmit,
  reviewValues,
  goToPage,
  showN1Questions,
  leaseDetails,
  change,
  cotenants,
  documentTemplates,
  additionalOccupants,
  agents,
  userId,
  claimUrl,
  clientId,
  validationErrors,
  showCreateRenewalSuccess,
  clearClaimUrl,
  hideServicesAndUtilities,
}) => {
  const style = {
    table_header: {
      margin: '30px 0px 10px 0px',
      backgroundColor: '#f1f1f1',
    },
  };

  const applicants = useMemo(() => cotenants.filter(c => !c.is_guarantor), [cotenants]);
  const guarantors = useMemo(() => cotenants.filter(c => c.is_guarantor), [cotenants]);

  useEffect(
    () => {
      const userHasPermission = agents.filter(agent => agent.attributes.id === userId).length > 0;
      if (userHasPermission) {
        // we need this AND the defaultValue props in the Select component for agent_id to work
        change('agent_id', userId);
      }
    },
    [userId, change, agents]
  );
  const userHasPermission = agents.filter(agent => agent.attributes.id === userId).length > 0;

  useEffect(
    () => {
      if (claimUrl) {
        hellosignClient.open(claimUrl, {
          clientId: clientId,
          skipDomainVerification: env.ENV !== 'production',
        });
      }
    },
    [clientId, claimUrl]
  );

  useEffect(
    () => {
      hellosignClient.on('send', () => {
        showCreateRenewalSuccess();
        clearClaimUrl();
      });
    },
    [showCreateRenewalSuccess, clearClaimUrl]
  );

  const renderHelloSignLeaseConfiguration = () => {
    const leaseTemplates = [];
    const otherTemplates = [];

    const numApplicants = applicants.length;
    const numGuarantors = guarantors.length;
    const numOccupants = additionalOccupants.length;

    const noSignaturesRequired = template => {
      return (
        template.signable_doc_type === 'other' &&
        template.max_num_applicants === 0 &&
        template.max_num_guarantors === 0 &&
        template.max_num_occupants === 0
      );
    };

    const filteredDocumentTemplates = documentTemplates.filter(
      template =>
        (numApplicants <= template.max_num_applicants &&
          numGuarantors <= template.max_num_guarantors &&
          numOccupants <= template.max_num_occupants &&
          template.status === 'published') ||
        noSignaturesRequired(template)
    );

    filteredDocumentTemplates.forEach(template => {
      if (template.signable_doc_type === 'lease') {
        leaseTemplates.push(template);
      } else {
        otherTemplates.push(template);
      }
    });

    return (
      <div>
        <h3 style={{ textAlign: 'center' }}>
          {getTranslatedString('landlordPortal.leaseCreator.setupSignatures')}
        </h3>
        <h4>{getTranslatedString('landlordPortal.leaseCreator.documents')}</h4>
        <Select
          name="lease_template_id"
          showSearch
          placeholder={getTranslatedString('landlordPortal.leaseCreator.leaseTemplate')}
          size="large"
          onChange={value => {
            change('lease_template_id', value);
          }}
          style={{ marginBottom: '8px' }}
        >
          {leaseTemplates.map(template => (
            <Select.Option value={template.id} key={`lease_template_${template.id}`}>
              {template.title}
            </Select.Option>
          ))}
        </Select>
        {validationErrors.lease_template_id && (
          <p style={{ color: colors.RED }}>{validationErrors.lease_template_id}</p>
        )}
        <Select
          name="other_template_ids"
          mode="multiple"
          showSearch
          placeholder={getTranslatedString('landlordPortal.leaseCreator.otherTemplate')}
          size="large"
          onChange={value => {
            change('other_template_ids', value);
          }}
          filterOption={(input, option) =>
            option.props.children.toLowerCase().includes(input.toLowerCase())}
          style={{ marginBottom: '8px' }}
        >
          {otherTemplates.map(template => (
            <Select.Option value={template.id} key={`other_template_${template.id}`}>
              {template.title}
            </Select.Option>
          ))}
        </Select>
        <h4>{getTranslatedString('landlordPortal.leaseCreator.applicants')}</h4>
        {applicants.map(applicant => (
          <div key={`applicant${applicant.id}`}>
            {applicant.first_name} {applicant.last_name} | {applicant.email}
          </div>
        ))}
        {applicants.length === 0 && <div>N/A</div>}
        <h4>{getTranslatedString('landlordPortal.leaseCreator.guarantors')}</h4>
        {guarantors.map(guarantor => (
          <div key={`guarantor${guarantor.id}`}>
            {guarantor.first_name} {guarantor.last_name} | {guarantor.email}
          </div>
        ))}
        {guarantors.length === 0 && <div>N/A</div>}
        <h4>{getTranslatedString('landlordPortal.leaseCreator.occupants')}</h4>
        {additionalOccupants.map(occupant => (
          <div key={`occupant${occupant.id}`}>
            {occupant.full_name} | {occupant.email}
          </div>
        ))}
        {additionalOccupants.length === 0 && <div>N/A</div>}
        <h4>{getTranslatedString('landlordPortal.leaseCreator.assignAgent')}</h4>
        <Select
          name="agent_id"
          showSearch
          placeholder={getTranslatedString('landlordPortal.leaseCreator.assignAgent')}
          size="large"
          defaultValue={userHasPermission ? userId : null}
          onChange={value => {
            change('agent_id', value);
          }}
        >
          {agents.map(agent => (
            <Select.Option value={agent.attributes.id} key={`agent_${agent.attributes.id}`}>
              {agent.attributes.first_name} {agent.attributes.last_name} | {agent.attributes.email}
            </Select.Option>
          ))}
        </Select>
        {validationErrors.agent_id && (
          <p style={{ color: colors.RED }}>{validationErrors.agent_id}</p>
        )}
      </div>
    );
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        id="form_field"
        encType="multipart/form-data"
        className="reviewForm"
      >
        {featureEnabled('custom_lease_creator') ? (
          renderHelloSignLeaseConfiguration()
        ) : (
          <div>
            <h3 style={{ textAlign: 'center' }}>
              {getTranslatedString('landlordPortal.reviewWizardPage.renewalReview')}
            </h3>
            <div className="row" style={style.table_header}>
              <div style={{ padding: '5px 10px 25px 15px' }}>
                <span style={{ textAlign: 'center', fontSize: '22px', float: 'left' }}>
                  {getTranslatedString('landlordPortal.reviewWizardPage.leaseTerms')}
                </span>
                <button
                  type="button"
                  className="link-button"
                  onClick={event => {
                    event.preventDefault();
                    goToPage(PAGES.LEASE_TERM);
                  }}
                  style={{ cursor: 'pointer', float: 'right' }}
                >
                  {' '}
                  {getTranslatedString('landlordPortal.reviewWizardPage.edit')}{' '}
                </button>
              </div>
            </div>
            <div className="row" style={{ marginTop: '0px' }}>
              <div className="col-sm-12">
                <div style={{ textAlign: 'center' }}>
                  <TextField
                    type="text"
                    disabled={true}
                    value={reviewValues.tenantRenewalOptions}
                    floatingLabelText={
                      <span>
                        <TenantResidentVerbiageHelper capitalize />{' '}
                        {getTranslatedString('landlordPortal.reviewWizardPage.renewalOptions')}
                      </span>
                    }
                    style={{
                      marginTop: '-15px',
                      fontSize: '14px',
                      width: '170px',
                    }}
                  />
                </div>
              </div>
            </div>

            {showN1Questions && (
              <div className="row" style={{ marginTop: '0px' }}>
                <div className="col-sm-12">
                  <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <div style={{ fontSize: '14px', color: '#bdbdbd' }}>
                      {getTranslatedString(
                        'landlordPortal.reviewWizardPage.n1RentIncreaseMoreThan'
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Field
                      name={'more_than_guideline'}
                      type="text"
                      disabled={true}
                      component={TextField}
                      inputStyle={{ textAlign: 'center' }}
                      style={{
                        marginTop: '-15px',
                        fontSize: '14px',
                        width: '170px',
                      }}
                    />
                  </div>
                  {reviewValues.moreThanGuideline === 'true' && (
                    <div>
                      <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <div style={{ fontSize: '14px', color: '#bdbdbd' }}>
                          {getTranslatedString(
                            'landlordPortal.reviewWizardPage.n1MoreThanGuideline'
                          )}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <Field
                          name={'has_been_approved'}
                          type="text"
                          disabled={true}
                          component={TextField}
                          inputStyle={{ textAlign: 'center' }}
                          style={{
                            marginTop: '-15px',
                            fontSize: '14px',
                            width: '170px',
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="row" style={{ marginTop: '15px' }}>
              <div className="col-sm-6">
                <Field
                  name={'lease_start_date'}
                  type="text"
                  disabled={true}
                  component={DatePicker}
                  openToYearSelection={true}
                  formatDate={formatter.reformatDate}
                  floatingLabelText={getTranslatedString(
                    'landlordPortal.reviewWizardPage.startLeaseDate'
                  )}
                  textFieldStyle={{ width: '170px', fontSize: '14px' }}
                  style={{ marginTop: '-15px', fontSize: '14px' }}
                />
              </div>
              <div className="col-sm-6">
                <Field
                  name={'lease_end_date'}
                  type="text"
                  disabled={true}
                  component={DatePicker}
                  openToYearSelection={true}
                  formatDate={formatter.reformatDate}
                  floatingLabelText={getTranslatedString(
                    'landlordPortal.reviewWizardPage.endLeaseDate'
                  )}
                  textFieldStyle={{ width: '170px', fontSize: '14px' }}
                  style={{ marginTop: '-15px', fontSize: '14px' }}
                />
              </div>
            </div>

            <div className="row" style={{ marginTop: '0px' }}>
              <div className="col-sm-12">
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <div style={{ fontSize: '14px', color: colors.DISABLED_GRAY }}>
                    {getTranslatedString('landlordPortal.reviewWizardPage.requireInsurance')}
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Field
                    name="renters_insurance_required"
                    type="text"
                    disabled
                    format={formatter.formatBoolean}
                    component={TextField}
                    inputStyle={{ textAlign: 'center' }}
                    style={{ marginTop: '-15px', fontSize: '14px', width: '170px' }}
                  />
                </div>
              </div>
            </div>

            {leaseDetails && leaseDetails.is_manual_renewal ? null : (
              <div>
                <div className="row" style={style.table_header}>
                  <div style={{ padding: '5px 10px 25px 15px' }}>
                    <span style={{ textAlign: 'center', fontSize: '22px', float: 'left' }}>
                      {' '}
                      Schedule A{' '}
                    </span>
                    <button
                      type="button"
                      className="link-button"
                      onClick={event => {
                        event.preventDefault();
                        goToPage(PAGES.SCHEDULE);
                      }}
                      style={{ cursor: 'pointer', float: 'right' }}
                    >
                      {' '}
                      edit{' '}
                    </button>
                  </div>
                </div>
                {reviewValues.schedule_a && (
                  <div className="row" style={{ marginTop: '15px' }}>
                    <div className="col-sm-12">
                      <div style={{ textAlign: 'center' }}>
                        <TextField
                          type="text"
                          disabled={true}
                          value={reviewValues.schedule_a}
                          floatingLabelText={getTranslatedString(
                            'landlordPortal.reviewWizardPage.scheduleAName'
                          )}
                          style={{
                            marginTop: '-15px',
                            fontSize: '14px',
                            width: '170px',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {reviewValues.schedule_a_name &&
                  reviewValues.schedule_a_body && (
                    <div className="row" style={{ marginTop: '15px' }}>
                      <div className="row" style={{ textAlign: 'center' }}>
                        <div className="col-sm-12">
                          <TextField
                            type="text"
                            disabled={true}
                            value={reviewValues.schedule_a_name}
                            floatingLabelText={getTranslatedString(
                              'landlordPortal.reviewWizardPage.scheduleAName'
                            )}
                            style={{
                              marginTop: '-5px',
                              fontSize: '14px',
                              width: '170px',
                            }}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-sm-12">
                          <TextField
                            type="text"
                            disabled={true}
                            value={reviewValues.schedule_a_body}
                            multiLine={true}
                            fullWidth
                            rows={5}
                            rowsMax={5}
                            floatingLabelText={getTranslatedString(
                              'landlordPortal.reviewWizardPage.scheduleAContents'
                            )}
                            style={{ marginTop: '-5px', fontSize: '14px' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            )}

            <div className="row" style={style.table_header}>
              <div style={{ padding: '5px 10px 25px 15px' }}>
                <span style={{ textAlign: 'center', fontSize: '22px', float: 'left' }}>
                  {' '}
                  {getTranslatedString('landlordPortal.reviewWizardPage.scheduleA')}{' '}
                </span>
                <button
                  type="button"
                  className="link-button"
                  onClick={event => {
                    event.preventDefault();
                    goToPage(PAGES.LEASE_PRICING);
                  }}
                  style={{ cursor: 'pointer', float: 'right' }}
                >
                  {' '}
                  {getTranslatedString('landlordPortal.reviewWizardPage.edit')}{' '}
                </button>
              </div>
            </div>
            <div className="row" style={{ marginTop: '15px' }}>
              <div className="col-sm-6">
                <Field
                  name={'monthly_rent'}
                  type="text"
                  disabled={true}
                  component={TextField}
                  floatingLabelText={getTranslatedString(
                    'landlordPortal.reviewWizardPage.monthlyRent'
                  )}
                  style={{ marginTop: '-15px', fontSize: '14px', width: '170px' }}
                />
              </div>
            </div>
            {(reviewValues.leaseItems || []).length > 0 && (
              <div className="row" style={{ marginTop: '0px' }}>
                <div className="col-sm-12" style={{ padding: '0 5px' }}>
                  <p
                    style={{
                      padding: '0 10px',
                      color: 'rgba(78, 78, 78, 0.9)',
                      fontSize: '13px',
                      margin: 0,
                      textAlign: 'center',
                    }}
                  >
                    {getTranslatedString('landlordPortal.reviewWizardPage.additionalLeaseItems')}
                  </p>
                  {RenderLeaseItem({ items: reviewValues.leaseItems, disabled: true })}
                </div>
              </div>
            )}

            <div className="row" style={style.table_header}>
              <div style={{ padding: '5px 10px 25px 15px' }}>
                <span style={{ textAlign: 'center', fontSize: '22px', float: 'left' }}>
                  {' '}
                  Rental Incentive (Optional){' '}
                </span>
                <button
                  type="button"
                  className="link-button"
                  onClick={event => {
                    event.preventDefault();
                    goToPage(PAGES.RENTAL_INCENTIVE);
                  }}
                  style={{ cursor: 'pointer', float: 'right' }}
                >
                  {' '}
                  {getTranslatedString('landlordPortal.reviewWizardPage.edit')}{' '}
                </button>
              </div>
            </div>
            {reviewValues.rentalIncentives &&
              reviewValues.rentalIncentives.map(rentalIncentive => {
                return (
                  <div className="row" style={{ marginTop: '15px' }}>
                    <div className="col-sm-4">
                      <ReviewField
                        title={getTranslatedString('landlordPortal.reviewWizardPage.startDate')}
                        value={formatter.toFriendlyDateString(rentalIncentive.effective_date)}
                      />
                    </div>
                    <div className="col-sm-4">
                      <ReviewField
                        title={getTranslatedString('landlordPortal.reviewWizardPage.frequency')}
                        value={rentalIncentive.frequency}
                      />
                    </div>
                    <div className="col-sm-4">
                      <ReviewField
                        title={getTranslatedString('landlordPortal.reviewWizardPage.monthlyAmount')}
                        value={formatter.formatMoney(rentalIncentive.incentive_amount)}
                      />
                    </div>
                  </div>
                );
              })}

            {!hideServicesAndUtilities && (
              <React.Fragment>
                <div className="row" style={style.table_header}>
                  <div style={{ padding: '5px 10px 25px 15px' }}>
                    <span style={{ textAlign: 'center', fontSize: '22px', float: 'left' }}>
                      Services and Utilities
                    </span>
                    <button
                      type="button"
                      className="link-button"
                      onClick={event => {
                        event.preventDefault();
                        goToPage(PAGES.SERVICES_AND_UTILITIES);
                      }}
                      style={{ cursor: 'pointer', float: 'right' }}
                    >
                      {' '}
                      edit{' '}
                    </button>
                  </div>
                </div>
                <div className="row" style={{ marginTop: '-20px', marginLeft: '4px' }}>
                  <ServicesAndUtilitiesWizardPage
                    form={'renewal_creator_form'}
                    isReviewMode={true}
                  />
                </div>
              </React.Fragment>
            )}
          </div>
        )}
      </form>
    </div>
  );
};
const mapStateToProps = (state, ownProps) => {
  return {
    form: ownProps.name,
  };
};

ReviewPackagePage = compose(
  connect(mapStateToProps),
  reduxForm({
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  })
)(ReviewPackagePage);

const selector = formValueSelector('renewal_creator_form');

let extractInitialValues = state => {
  const schedule_a = selector(state, 'schedule_a');
  const schedule_a_body = selector(state, 'schedule_a_body');
  const schedule_a_name = selector(state, 'schedule_a_name');
  const moreThanGuideline = selector(state, 'more_than_guideline');
  const tenantRenewalOptions = selector(state, 'tenant_renewal_options');
  const leaseItems = selector(state, 'lease_items');
  const rentalIncentives = selector(state, 'rental_incentives') || [];
  const cotenants = state.leaseDetails.leaseDetails.cotenants;
  const additionalOccupants = state.leaseDetails.leaseDetails.additional_occupants;
  const documentTemplates = state.landlordDocumentTemplates.documentTemplates;
  const agents = state.leaseDetails.leaseDetails.agents;
  const userId = state.userTypes.userId;
  const claimUrl = state.landlordApplications.claimUrl;
  const clientId = state.landlordSignableDoc.clientId;

  return {
    cotenants,
    documentTemplates,
    additionalOccupants,
    agents,
    userId,
    claimUrl,
    clientId,
    reviewValues: {
      schedule_a,
      schedule_a_body,
      schedule_a_name,
      moreThanGuideline,
      tenantRenewalOptions,
      leaseItems,
      rentalIncentives,
    },
  };
};

ReviewPackagePage = connect(
  state => {
    return extractInitialValues(state);
  },
  {
    showCreateRenewalSuccess,
    clearClaimUrl,
  }
)(ReviewPackagePage);

export default ReviewPackagePage;
